import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AD_MANAGEMENT_API } from 'configs/api/adManagement';
import { useCountry, useHttpClient } from 'hooks';
import { FORM_VALIDATORS } from 'utils/FormValidators';
import { disableWheelChange } from 'utils/InputComponent';
import { IAdSectionCompetitors } from 'types/adManagement';
import { useAdBid } from '../useAdBid';
import { Card, ConfigProvider, Flex, Form, InputNumber, InputNumberProps, Slider, Tooltip } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { ArrowDownOutlined } from '@ant-design/icons';

const useCompetitorsBids = (adSectionId?: number) => {
	const { vendorId } = useAdBid();

	// ! http clients
	const fetchCompetitorsBidInformation = useHttpClient<IAdSectionCompetitors>();

	// ! effects
	useEffect(
		() => {
			if (!adSectionId) return;

			fetchCompetitorsBidInformation.request({
				requestConfig: AD_MANAGEMENT_API.getCompetitorsInfo(vendorId, adSectionId),
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[adSectionId]
	);

	//
	return {
		...fetchCompetitorsBidInformation.response,
	};
};

const AD_BID_PRICE_PER_CLICK_CONFIGS = {
	MIN_BID: 0,
	MAX_BID: 0.75,
};

export const BidInput: FC<{ adSectionId?: number } & InputNumberProps> = ({ adSectionId, ...props }) => {
	const { minimum_bid: minConcurrent, maximum_bid: maxConcurrent } = useCompetitorsBids(adSectionId);

	const currentBid = useWatch('price_per_click');
	const ad_section_id = useWatch('ad_section_id');

	const { selectedCountryCurrencyCode } = useCountry();

	const { t: tAdManagement } = useTranslation('ad-management');

	// ! memos
	const competitorsMeanBid = !!minConcurrent && !!maxConcurrent ? (minConcurrent + maxConcurrent) / 2 : 0;

	const { MAX_BID, MIN_BID } = useMemo(() => {
		const resultingConfigs = { ...AD_BID_PRICE_PER_CLICK_CONFIGS };

		if (ad_section_id?.minimum_bid) {
			resultingConfigs.MIN_BID = ad_section_id.minimum_bid;
		}

		return resultingConfigs;
	}, [ad_section_id]);

	// ! render
	return (
		<>
			<Card>
				<Flex vertical>
					<Form.Item
						name='price_per_click'
						label={tAdManagement('form.bid.label')}
						extra={tAdManagement('form.bid.extra')}
						rules={[
							FORM_VALIDATORS.REQUIRED(),
							FORM_VALIDATORS.NUMBER_GREATER_EQUAL_THAN(MIN_BID),
							FORM_VALIDATORS.NUMBER_LESSER_EQUAL_THAN(MAX_BID),
						]}
					>
						<InputNumber
							placeholder={tAdManagement('form.bid.placeholder')}
							min={0}
							// max={MAX_BID}
							step={0.01}
							type='number'
							suffix={selectedCountryCurrencyCode}
							className='w-100'
							onWheel={disableWheelChange}
							{...props}
						/>
					</Form.Item>

					{!!minConcurrent && !!maxConcurrent && (
						<div style={{ padding: '20px 28px 14px 28px' }}>
							<ConfigProvider
								theme={{
									components: {
										Slider: {
											controlSize: 8,
											railSize: 8,
											handleSize: 10,
											handleLineWidth: 2,
											handleLineWidthHover: 4,
										},
									},
								}}
							>
								<Slider
									range
									value={[minConcurrent, maxConcurrent]}
									marks={{
										[competitorsMeanBid]: {
											style: { height: 0 },
											label: (
												<Tooltip
													open
													color='var(--ant-primary-2)'
													title={tAdManagement('form.bid.competition_range')}
													placement='bottom'
													autoAdjustOverflow={false}
												/>
											),
										},
										[MIN_BID]: {
											label: (
												<Flex vertical>
													<div>{MIN_BID}</div>
													<div>min</div>
												</Flex>
											),
										},
										[MAX_BID]: {
											label: (
												<Flex vertical>
													<div>{MAX_BID}</div>
													<div>max</div>
												</Flex>
											),
										},
										[currentBid]: {
											style: { color: 'var(--ant-primary-color)', bottom: 20 },
											label: (
												<Flex
													align='center'
													vertical
												>
													<span>{tAdManagement('form.bid.feedback_mark')}</span>
													<ArrowDownOutlined />
												</Flex>
											),
										},
									}}
									min={0}
									max={MAX_BID}
									step={0.001}
								/>
							</ConfigProvider>
						</div>
					)}
				</Flex>
			</Card>
		</>
	);
};
